import { supabase } from '../lib/supabase';

/**
 * Decision Service
 * Handles chair decision management operations
 * Converts between snake_case (database) and camelCase (React)
 */

export const decisionService = {
  /**
   * Get all submissions with reviews for a conference
   */
  async getSubmissionsWithReviews(conferenceId) {
    try {
      // Fetch all submissions for the conference
      const { data: submissions, error: submissionsError } = await supabase
        ?.from('conference_submissions')
        ?.select(`
          *,
          user_profiles (first_name, last_name, email, institution),
          conference_tracks (name)
        `)
        ?.eq('conference_id', conferenceId)
        ?.eq('status', 'submitted')
        ?.order('submitted_at', { ascending: false });

      if (submissionsError) throw submissionsError;

      // Fetch all reviews for these submissions
      const { data: reviews, error: reviewsError } = await supabase
        ?.from('submission_reviews')
        ?.select(`
          *,
          user_profiles (first_name, last_name, email, institution)
        `)
        ?.eq('conference_id', conferenceId)
        ?.eq('is_draft', false);

      if (reviewsError) throw reviewsError;

      // Fetch review criteria for the conference
      const { data: criteria, error: criteriaError } = await supabase
        ?.from('review_criteria')
        ?.select('*')
        ?.eq('conference_id', conferenceId)
        ?.order('display_order', { ascending: true });

      if (criteriaError) throw criteriaError;

      // Fetch all review answers
      const submissionIds = submissions?.map(s => s?.id) || [];
      const { data: answers, error: answersError } = await supabase
        ?.from('review_answers')
        ?.select('*')
        ?.in('submission_id', submissionIds);

      if (answersError) throw answersError;

      // Group reviews and answers by submission
      const submissionsWithReviews = submissions?.map(sub => {
        const submissionReviews = reviews?.filter(r => r?.submission_id === sub?.id) || [];
        const submissionAnswers = answers?.filter(a => a?.submission_id === sub?.id) || [];

        // Calculate aggregate scores
        const scores = this.calculateAggregateScores(submissionReviews);
        
        // Detect conflicts
        const conflicts = this.detectConflicts(submissionReviews);

        // Map reviews with reviewer info
        const reviewsWithDetails = submissionReviews?.map(review => {
          const reviewerAnswers = submissionAnswers?.filter(a => a?.reviewer_id === review?.reviewer_id) || [];
          
          return {
            id: review?.id,
            reviewerId: review?.reviewer_id,
            reviewerName: `${review?.user_profiles?.first_name} ${review?.user_profiles?.last_name}`,
            reviewerEmail: review?.user_profiles?.email,
            reviewerInstitution: review?.user_profiles?.institution,
            overallScore: review?.overall_score,
            originalityScore: review?.originality_score,
            technicalQualityScore: review?.technical_quality_score,
            clarityScore: review?.clarity_score,
            relevanceScore: review?.relevance_score,
            detailedFeedback: review?.detailed_feedback,
            strengths: review?.strengths,
            weaknesses: review?.weaknesses,
            questionsForAuthors: review?.questions_for_authors,
            decision: review?.decision,
            confidenceLevel: review?.confidence_level,
            generalComments: review?.general_comments,
            submittedAt: review?.submitted_at,
            criteriaAnswers: reviewerAnswers?.map(ans => ({
              criteriaId: ans?.criteria_id,
              answerNumeric: ans?.answer_numeric,
              answerBoolean: ans?.answer_boolean,
              answerText: ans?.answer_text
            }))
          };
        });

        return {
          id: sub?.id,
          conferenceId: sub?.conference_id,
          title: sub?.title,
          abstract: sub?.abstract,
          keywords: sub?.keywords,
          trackId: sub?.track_id,
          trackName: sub?.conference_tracks?.name,
          status: sub?.status,
          submittedAt: sub?.submitted_at,
          author: {
            firstName: sub?.user_profiles?.first_name,
            lastName: sub?.user_profiles?.last_name,
            email: sub?.user_profiles?.email,
            institution: sub?.user_profiles?.institution
          },
          reviews: reviewsWithDetails,
          aggregateScores: scores,
          conflicts: conflicts,
          reviewCount: submissionReviews?.length || 0
        };
      }) || [];

      return {
        submissions: submissionsWithReviews,
        criteria: criteria?.map(c => ({
          id: c?.id,
          questionText: c?.question_text,
          questionType: c?.question_type,
          displayOrder: c?.display_order
        })) || []
      };
    } catch (error) {
      console.error('Get submissions with reviews error:', error);
      throw error;
    }
  },

  /**
   * Calculate aggregate scores from multiple reviews
   */
  calculateAggregateScores(reviews) {
    if (!reviews || reviews?.length === 0) {
      return {
        avgOverall: 0,
        avgOriginality: 0,
        avgTechnicalQuality: 0,
        avgClarity: 0,
        avgRelevance: 0,
        varianceOverall: 0
      };
    }

    const scores = {
      overall: reviews?.map(r => r?.overall_score)?.filter(s => s != null),
      originality: reviews?.map(r => r?.originality_score)?.filter(s => s != null),
      technicalQuality: reviews?.map(r => r?.technical_quality_score)?.filter(s => s != null),
      clarity: reviews?.map(r => r?.clarity_score)?.filter(s => s != null),
      relevance: reviews?.map(r => r?.relevance_score)?.filter(s => s != null)
    };

    const avg = (arr) => arr?.length > 0 ? arr?.reduce((a, b) => a + b, 0) / arr?.length : 0;
    const variance = (arr) => {
      if (arr?.length < 2) return 0;
      const mean = avg(arr);
      return Math.sqrt(arr?.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / arr?.length);
    };

    return {
      avgOverall: avg(scores?.overall),
      avgOriginality: avg(scores?.originality),
      avgTechnicalQuality: avg(scores?.technicalQuality),
      avgClarity: avg(scores?.clarity),
      avgRelevance: avg(scores?.relevance),
      varianceOverall: variance(scores?.overall)
    };
  },

  /**
   * Detect conflicts in reviews (high variance in scores)
   */
  detectConflicts(reviews) {
    if (!reviews || reviews?.length < 2) {
      return [];
    }

    const conflicts = [];
    const scores = reviews?.map(r => r?.overall_score)?.filter(s => s != null);
    
    if (scores?.length < 2) return conflicts;

    const max = Math.max(...scores);
    const min = Math.min(...scores);
    const range = max - min;

    // Flag as conflict if score range is >= 4 points (on 1-10 scale)
    if (range >= 4) {
      conflicts?.push({
        type: 'score_variance',
        severity: 'high',
        message: `High variance in overall scores (range: ${range} points)`,
        details: { min, max, range }
      });
    }

    // Check for conflicting decisions
    const decisions = reviews?.map(r => r?.decision)?.filter(d => d != null);
    const acceptDecisions = decisions?.filter(d => d === 'accept' || d === 'minor_revision')?.length;
    const rejectDecisions = decisions?.filter(d => d === 'reject' || d === 'major_revision')?.length;

    if (acceptDecisions > 0 && rejectDecisions > 0) {
      conflicts?.push({
        type: 'decision_conflict',
        severity: 'medium',
        message: 'Reviewers have conflicting recommendations',
        details: { acceptCount: acceptDecisions, rejectCount: rejectDecisions }
      });
    }

    return conflicts;
  },

  // generateRecommendation() used to live here: it read the average score and
  // suggested accept / minor / major / reject from fixed thresholds. Removed
  // with the Decisions screen. The thresholds were never validated against real
  // conferences, and a confident wrong suggestion next to a decision button is
  // worse than no suggestion. The reviewer-disagreement signal it computed was
  // the useful part and now lives in utils/reviewAgreement.js.

  /**
   * Update decision for a submission
   */
  /**
   * Record a chair's decision on a paper.
   *
   * `decision` is a submission_status value ('accepted' | 'rejected'), NOT a
   * reviewer recommendation — the caller maps it. `options.severity` records
   * that an acceptance is conditional on changes; the paper is accepted either
   * way, so status stays 'accepted' and the severity is what distinguishes
   * "accepted as-is" from "accepted, please revise".
   */
  async updateDecision(submissionId, decision, options = {}) {
    try {
      const { data: submission, error: fetchError } = await supabase
        ?.from('conference_submissions')
        ?.select(`
          *,
          user_profiles (email, first_name, last_name),
          conferences (name)
        `)
        ?.eq('id', submissionId)
        ?.maybeSingle();

      if (fetchError) throw fetchError;
      if (!submission) {
        throw new Error('Submission not found or access denied');
      }

      const { severity = null, note = null, email = null } = options || {};

      const patch = {
        status: decision,
        updated_at: new Date()?.toISOString(),
        // Always write these, including the nulls: clearing a previous
        // conditional acceptance is as meaningful as setting one.
        revision_severity: severity,
        revision_note: severity ? note : null,
        revision_requested_at: severity ? new Date()?.toISOString() : null,
      };

      const { error } = await supabase
        ?.from('conference_submissions')
        ?.update(patch)
        ?.eq('id', submissionId);

      if (error) throw error;

      // Send email notification to author
      try {
        const author = submission?.user_profiles;
        const conference = submission?.conferences;

        if (author?.email && conference?.name) {
          // Import notification service dynamically to avoid circular dependency
          const { notificationService } = await import('./notificationService.js');
          
          if (email?.subject && email?.message) {
            // The chair reviewed (and possibly edited) this exact text in the
            // decision dialog. Send what they saw, not a regenerated version.
            await notificationService?.sendCustomEmail({
              to: author?.email,
              subject: email.subject,
              message: email.message,
              conferenceId: submission?.conference_id,
            });
          } else {
            await notificationService?.sendDecisionEmail(
              author?.email,
              `${author?.first_name} ${author?.last_name}`,
              submission?.title,
              decision,
              conference?.name,
              submission?.conference_id
            );
          }
        }
      } catch (emailError) {
        console.error('Failed to send decision email:', emailError);
        // Don't fail the decision update if email fails
      }

      return { ...submission, ...patch };
    } catch (error) {
      console.error('Update decision error:', error);
      throw error;
    }
  },

  /**
   * Bulk update decisions for multiple submissions
   */
  async bulkUpdateDecisions(decisions) {
    try {
      const results = [];
      const errors = [];

      for (const { submissionId, decision } of decisions) {
        try {
          const { data: submission, error: fetchError } = await supabase
            ?.from('conference_submissions')
            ?.select(`
              *,
              user_profiles (email, first_name, last_name),
              conferences (name)
            `)
            ?.eq('id', submissionId)
            ?.maybeSingle();

          if (fetchError) throw fetchError;
          if (!submission) {
            throw new Error('Submission not found or access denied');
          }

          const { error } = await supabase
            ?.from('conference_submissions')
            ?.update({
              status: decision,
              updated_at: new Date()?.toISOString()
            })
            ?.eq('id', submissionId);

          if (error) throw error;

          results?.push({ submissionId, success: true });

          // Send email notification to author
          try {
            const author = submission?.user_profiles;
            const conference = submission?.conferences;

            if (author?.email && conference?.name) {
              // Import notification service dynamically to avoid circular dependency
              const { notificationService } = await import('./notificationService.js');
              
              // Send email
              await notificationService?.sendDecisionEmail(
                author?.email,
                `${author?.first_name} ${author?.last_name}`,
                submission?.title,
                decision,
                conference?.name,
                submission?.conference_id
              );
            }
          } catch (emailError) {
            console.error('Failed to send decision email:', emailError);
            // Don't fail the decision update if email fails
          }
        } catch (error) {
          console.error(`Error updating decision for ${submissionId}:`, error);
          errors?.push({ submissionId, error: error?.message });
        }
      }

      return { results, errors };
    } catch (error) {
      console.error('Bulk update decisions error:', error);
      throw error;
    }
  }
};

export default decisionService;
